/**
 * Marca de agua del evento: geometría y vocabulario compartidos con el backend.
 *
 * La web sube SOLO el logo original a `public/{eventId}/Configuration/Watermark.png` con los
 * ajustes (posición, tamaño, opacidad, activa, si hay logo propio) en los metadatos S3. El backend
 * (`sam-revopic/src/Services/Photos/PhotoRepository.cs → ProcessSponsorAsync`, con
 * `WatermarkPlacement.cs` como espejo exacto de este fichero) auto-orienta la miniatura (lado
 * largo 960 px), escala el logo al porcentaje del ancho sin ampliarlo y lo ancla según la
 * posición real de cada foto. Esa subida dispara el re-sellado de todas las miniaturas del evento.
 * La vista previa pinta esta misma geometría sobre dos fotos de muestra 3:2.
 * Cualquier cambio de tokens o de geometría va en los dos sitios.
 */

export const POSITIONS = ['tl', 'tr', 'bl', 'br', 'center'];
export const SIZES = { s: 15, m: 25, l: 40, xl: 50, '2xl': 60, '3xl': 80, '4xl': 100 };
export const OPACITIES = { lo: 30, mid: 50, hi: 70, full: 100 };

/** Lado largo de la miniatura (horizontal: 960 de ancho) sobre el que se mide el tamaño. */
const THUMB_LONG_SIDE = 960;

export const DEFAULT_SETTINGS = { position: 'br', size: 'm', opacity: 'mid', enabled: true };

/** Ancho mínimo del logo para que, con ese tamaño, no haya que ampliarlo: 25 % → 240 px, 100 % → 960 px. */
export const minLogoWidth = (size) => Math.ceil(THUMB_LONG_SIDE * ((SIZES[size] || SIZES[DEFAULT_SETTINGS.size]) / 100));

/**
 * Marca de Revopic: lo que el backend pega cuando el evento no tiene logo propio. Es el mismo
 * PNG que `default/Sponsor{Landscape,Portrait}Revopic.png` del bucket de fotos (idéntico en los
 * tres entornos), un overlay a pantalla completa 960×640 / 640×960 anclado abajo-derecha.
 * Se sirve empaquetado para la vista previa; posición, tamaño y opacidad no le aplican.
 */
export const DEFAULT_OVERLAY = {
    landscape: `${process.env.PUBLIC_URL || ''}/img/watermark-revopic-landscape.png`,
    portrait: `${process.env.PUBLIC_URL || ''}/img/watermark-revopic-portrait.png`,
};

/**
 * PNG transparente de 1×1: el objeto Watermark.png que se sube cuando no hay logo propio y solo
 * hay que guardar ajustes (apagar o volver a activar la marca de Revopic). Va con `logo: none`
 * en los metadatos; el backend entonces no lo usa como logo.
 */
const PLACEHOLDER_PNG_BASE64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
export const placeholderPng = () => {
    const bytes = Uint8Array.from(atob(PLACEHOLDER_PNG_BASE64), (c) => c.charCodeAt(0));
    return new Blob([bytes], { type: 'image/png' });
};

/** Margen a los bordes en las esquinas, como fracción del ancho de la foto. */
const MARGIN_RATIO = 0.025;

/** Fotos de muestra de la vista previa (public/img), con las medidas de una miniatura real 3:2. */
export const SAMPLE = {
    landscape: { src: `${process.env.PUBLIC_URL || ''}/img/watermark-sample-landscape.jpg`, width: 960, height: 640 },
    portrait: { src: `${process.env.PUBLIC_URL || ''}/img/watermark-sample-portrait.jpg`, width: 640, height: 960 },
};

/**
 * Rectángulo donde va el logo dentro de la foto (miniatura ya orientada).
 * El ancho es `scalePct` % del ancho de la foto, sin ampliar nunca (tope: ancho natural) y sin
 * salirse de la foto (tope: la foto menos los márgenes, en ancho y en alto).
 */
export const placeLogo = ({ canvasW, canvasH, logoW, logoH, position, scalePct }) => {
    const margin = Math.round(canvasW * MARGIN_RATIO);
    const target = canvasW * (scalePct / 100);
    let width = Math.min(target, logoW, canvasW - 2 * margin);
    const maxH = canvasH - 2 * margin;
    if (width * logoH / logoW > maxH) width = maxH * logoW / logoH;
    const w = Math.round(width);
    const h = Math.round(w * (logoH / logoW));
    let x;
    let y;
    switch (position) {
        case 'tl':
            x = margin;
            y = margin;
            break;
        case 'tr':
            x = canvasW - w - margin;
            y = margin;
            break;
        case 'bl':
            x = margin;
            y = canvasH - h - margin;
            break;
        case 'center':
            x = Math.round((canvasW - w) / 2);
            y = Math.round((canvasH - h) / 2);
            break;
        case 'br':
        default:
            x = canvasW - w - margin;
            y = canvasH - h - margin;
            break;
    }
    return { x, y, w, h };
};

/**
 * Estilo inline del logo en la vista previa, en % de la foto de muestra: la misma geometría que
 * aplicará el backend sobre una miniatura de esas medidas (incluido el tope «no se amplía»).
 * `logo` es `{ width, height }` (medidas naturales del PNG).
 */
export const previewStyle = (logo, orientation, settings) => {
    const { width, height } = SAMPLE[orientation];
    const { x, y, w, h } = placeLogo({
        canvasW: width,
        canvasH: height,
        logoW: logo.width,
        logoH: logo.height,
        position: settings.position,
        scalePct: SIZES[settings.size],
    });
    const pct = (value, total) => `${(value / total) * 100}%`;
    return {
        left: pct(x, width),
        top: pct(y, height),
        width: pct(w, width),
        height: pct(h, height),
        opacity: OPACITIES[settings.opacity] / 100,
    };
};

/** Carga un Blob/File de imagen como HTMLImageElement. */
export const loadImage = (blob) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
    };
    img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('No se pudo decodificar la imagen'));
    };
    img.src = url;
});

/**
 * Inspecciona un PNG ya cargado: medidas y si tiene algún píxel con transparencia.
 * Muestrea el canal alfa sobre una reducción de hasta 256 px de lado (barato y suficiente).
 */
export const inspectImage = (img) => {
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    const scale = Math.min(1, 256 / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    let hasAlpha = false;
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
            hasAlpha = true;
            break;
        }
    }
    return { width, height, hasAlpha };
};

/**
 * Ajustes → metadatos S3 (solo cadenas). Los lee `WatermarkPlacement.FromMetadata` en el backend.
 * `customLogo: false` marca el objeto como portador de ajustes sin logo propio (`logo: none`).
 */
export const settingsToMetadata = (settings, { customLogo = true } = {}) => ({
    position: settings.position,
    size: settings.size,
    opacity: settings.opacity,
    enabled: settings.enabled ? 'true' : 'false',
    logo: customLogo ? 'custom' : 'none',
    source: 'watermark-tab',
});

/** Un Watermark.png guardado lleva logo propio salvo que sus metadatos digan `logo: none`. */
export const hasCustomLogo = (metadata) => (metadata || {}).logo !== 'none';

/** Metadatos S3 (claves en minúsculas) → ajustes; lo que no cuadre cae al valor por defecto. */
export const metadataToSettings = (metadata) => {
    const m = metadata || {};
    return {
        position: POSITIONS.includes(m.position) ? m.position : DEFAULT_SETTINGS.position,
        size: SIZES[m.size] ? m.size : DEFAULT_SETTINGS.size,
        opacity: OPACITIES[m.opacity] ? m.opacity : DEFAULT_SETTINGS.opacity,
        enabled: m.enabled !== 'false',
    };
};
